import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Phone, Mail, MapPin, Building2, Upload, X, Check, FileText, MessageCircle, Image as ImageIcon, Palette } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const WHATSAPP_LINK = "https://wa.me/33643693688?text=Bonjour%2C%20je%20souhaite%20un%20devis%20pour%20une%20enseigne.";

const signTypes = [
  "Enseigne lumineuse LED", "Lettres découpées", "Caisson lumineux", "Lettres boîtier",
  "Totem lumineux", "Néon LED", "Habillage vitrine", "Store banne",
  "Covering véhicule", "Impression grand format", "Autre"
];

export function QuoteFormSection({ formRef }) {
  const [formData, setFormData] = useState({
    name: "", company: "", phone: "", email: "", city: "", sign_type: "", project_description: ""
  });
  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);

  const handleChange = (e) => setFormData(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleFileChange = (e, type) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      if (type === "photo") { setPhotoFile(file); setPhotoPreview(reader.result); }
      else { setLogoFile(file); setLogoPreview(reader.result); }
    };
    reader.readAsDataURL(file);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      let photoUrl = null;
      if (photoFile) {
        photoUrl = await new Promise(r => { const fr = new FileReader(); fr.onloadend = () => r(fr.result); fr.readAsDataURL(photoFile); });
      }
      const description = [
        formData.company ? `Entreprise: ${formData.company}` : "",
        formData.sign_type ? `Type: ${formData.sign_type}` : "",
        formData.project_description
      ].filter(Boolean).join("\n");

      await axios.post(`${API}/simulation`, {
        name: formData.name, phone: formData.phone, email: formData.email,
        city: formData.city, project_description: description, photo_url: photoUrl
      });
      setIsSubmitted(true);
      if (window.gtag) {
        window.gtag('event', 'conversion', { send_to: 'AW-9775626347/formulaire_simulation' });
        window.gtag('event', 'generate_lead', { currency: 'EUR', value: 500 });
      }
      toast.success("Demande envoyée !");
    } catch { toast.error("Erreur, veuillez réessayer."); }
    finally { setIsSubmitting(false); }
  };

  if (isSubmitted) {
    return (
      <section ref={formRef} className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-[#050505]" id="devis" data-testid="form-section">
        <div className="max-w-2xl mx-auto">
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
            className="bg-[#121212] border border-[#00FF66]/30 rounded-3xl p-10 text-center" data-testid="form-success">
            <div className="w-20 h-20 mx-auto bg-[#00FF66]/20 rounded-full flex items-center justify-center mb-6">
              <Check className="w-10 h-10 text-[#00FF66]" />
            </div>
            <h3 className="font-['Clash_Display',sans-serif] text-2xl font-bold text-white mb-3">Demande envoyée !</h3>
            <p className="text-zinc-400 mb-6">Notre équipe vous recontacte sous 24h avec votre devis personnalisé.</p>
            <a href={WHATSAPP_LINK} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-[#25D366] text-white px-8 py-4 rounded-xl font-bold hover:shadow-[0_10px_30px_rgba(37,211,102,0.3)] transition-all">
              <MessageCircle className="w-5 h-5" /> Réponse plus rapide sur WhatsApp
            </a>
            <button onClick={() => { setIsSubmitted(false); setFormData({ name:"",company:"",phone:"",email:"",city:"",sign_type:"",project_description:"" }); setPhotoFile(null); setPhotoPreview(null); }}
              className="block mx-auto mt-4 text-zinc-600 text-sm hover:text-zinc-400">Nouvelle demande</button>
          </motion.div>
        </div>
      </section>
    );
  }

  return (
    <section ref={formRef} className="py-24 sm:py-32 px-4 sm:px-6 lg:px-8 bg-[#050505]" id="devis" data-testid="form-section">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-12">
          <p className="text-sm uppercase tracking-[0.3em] font-medium text-[#00FF66] mb-4 font-['Outfit',sans-serif]">DEVIS GRATUIT</p>
          <h2 className="font-['Clash_Display',sans-serif] text-3xl sm:text-4xl font-semibold tracking-tight text-white">
            Recevez votre devis sous 24h
          </h2>
          <p className="mt-4 text-zinc-400 font-['Outfit',sans-serif]">
            Sans engagement · Conseil gratuit · Estimation personnalisée
          </p>
        </div>

        <div className="bg-[#121212] border border-[#262626] rounded-3xl p-6 sm:p-10">
          <form onSubmit={handleSubmit} className="space-y-5" data-testid="quote-form">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="relative">
                <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                <Input name="name" placeholder="Nom / Société *" value={formData.name} onChange={handleChange} required
                  className="pl-10 bg-[#0a0a0a] border-[#262626] h-12 text-white placeholder:text-zinc-600 focus:border-[#00FF66] focus:ring-[#00FF66]/20 rounded-xl" data-testid="form-name" />
              </div>
              <div className="relative">
                <Building2 className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                <Input name="company" placeholder="Nom de l'entreprise" value={formData.company} onChange={handleChange}
                  className="pl-10 bg-[#0a0a0a] border-[#262626] h-12 text-white placeholder:text-zinc-600 focus:border-[#00FF66] focus:ring-[#00FF66]/20 rounded-xl" data-testid="form-company" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="relative">
                <Phone className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                <Input name="phone" type="tel" placeholder="Téléphone *" value={formData.phone} onChange={handleChange} required
                  className="pl-10 bg-[#0a0a0a] border-[#262626] h-12 text-white placeholder:text-zinc-600 focus:border-[#00FF66] focus:ring-[#00FF66]/20 rounded-xl" data-testid="form-phone" />
              </div>
              <div className="relative">
                <Mail className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                <Input name="email" type="email" placeholder="Email *" value={formData.email} onChange={handleChange} required
                  className="pl-10 bg-[#0a0a0a] border-[#262626] h-12 text-white placeholder:text-zinc-600 focus:border-[#00FF66] focus:ring-[#00FF66]/20 rounded-xl" data-testid="form-email" />
              </div>
            </div>
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="relative">
                <MapPin className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-600" />
                <Input name="city" placeholder="Ville *" value={formData.city} onChange={handleChange} required
                  className="pl-10 bg-[#0a0a0a] border-[#262626] h-12 text-white placeholder:text-zinc-600 focus:border-[#00FF66] focus:ring-[#00FF66]/20 rounded-xl" data-testid="form-city" />
              </div>
              <select name="sign_type" value={formData.sign_type} onChange={handleChange}
                className="bg-[#0a0a0a] border border-[#262626] h-12 text-white rounded-xl px-4 focus:border-[#00FF66] focus:ring-[#00FF66]/20 text-sm"
                data-testid="form-sign-type">
                <option value="" className="text-zinc-600">Type d'enseigne</option>
                {signTypes.map(t => <option key={t} value={t}>{t}</option>)}
              </select>
            </div>
            <Textarea name="project_description" placeholder="Décrivez votre projet : dimensions souhaitées, couleurs, style..." value={formData.project_description} onChange={handleChange} rows={4}
              className="bg-[#0a0a0a] border-[#262626] text-white placeholder:text-zinc-600 focus:border-[#00FF66] focus:ring-[#00FF66]/20 resize-none rounded-xl" data-testid="form-description" />

            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs text-zinc-500 mb-2 flex items-center gap-1.5"><ImageIcon className="w-3.5 h-3.5" /> Photo de la façade</label>
                <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, "photo")} className="hidden" id="photo-up" data-testid="form-photo" />
                <label htmlFor="photo-up" className="flex items-center justify-center gap-2 h-20 border border-dashed border-[#262626] rounded-xl cursor-pointer hover:border-[#00FF66]/50 bg-[#0a0a0a] transition-colors">
                  {photoPreview ? (
                    <div className="relative w-full h-full"><img src={photoPreview} alt="" className="w-full h-full object-cover rounded-xl" />
                      <button type="button" onClick={(e) => { e.preventDefault(); setPhotoFile(null); setPhotoPreview(null); }} className="absolute top-1 right-1 bg-red-500 text-white p-0.5 rounded-full"><X className="w-3 h-3" /></button>
                    </div>
                  ) : <span className="text-zinc-600 text-sm flex items-center gap-2"><Upload className="w-4 h-4" /> Ajouter une photo</span>}
                </label>
              </div>
              <div>
                <label className="block text-xs text-zinc-500 mb-2 flex items-center gap-1.5"><Palette className="w-3.5 h-3.5" /> Logo (optionnel)</label>
                <input type="file" accept="image/*,.pdf,.ai,.eps,.svg" onChange={(e) => handleFileChange(e, "logo")} className="hidden" id="logo-up" data-testid="form-logo" />
                <label htmlFor="logo-up" className="flex items-center justify-center gap-2 h-20 border border-dashed border-[#262626] rounded-xl cursor-pointer hover:border-[#00FF66]/50 bg-[#0a0a0a] transition-colors">
                  {logoPreview ? (
                    <div className="relative w-full h-full"><img src={logoPreview} alt="" className="w-full h-full object-contain rounded-xl p-2" />
                      <button type="button" onClick={(e) => { e.preventDefault(); setLogoFile(null); setLogoPreview(null); }} className="absolute top-1 right-1 bg-red-500 text-white p-0.5 rounded-full"><X className="w-3 h-3" /></button>
                    </div>
                  ) : <span className="text-zinc-600 text-sm flex items-center gap-2"><Upload className="w-4 h-4" /> Ajouter un logo</span>}
                </label>
              </div>
            </div>

            <Button type="submit" disabled={isSubmitting}
              className="w-full bg-[#00FF66] text-black hover:bg-[#00dd55] h-14 text-lg font-bold rounded-xl transition-all duration-300 active:scale-[0.98] disabled:opacity-50 shadow-[0_0_30px_rgba(0,255,102,0.2)] hover:shadow-[0_0_40px_rgba(0,255,102,0.4)]"
              data-testid="form-submit-btn">
              {isSubmitting ? "Envoi en cours..." : "Recevoir mon devis gratuit"}
            </Button>
            <p className="text-xs text-zinc-600 text-center">Sans engagement · Vos données restent confidentielles</p>
          </form>
        </div>
      </div>
    </section>
  );
}
